import { useState } from 'react'

import { Box, Button, Card, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { TrainingsReminderTimeframeEnum } from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Select from 'modules/core/components/Select'
import TextEditor from 'modules/core/components/TextEditor'
import TextField from 'modules/core/components/TextField'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SectionButton from 'modules/questionnaires/components/Section/SectionButton'
import { ReminderValues } from 'modules/trainings/components/NewReminder/useNewReminder'
import { SessionData } from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'
import { useSessionsForm } from 'modules/trainings/hooks/Sessions/useSessionsForm'
import {
  useCreateReminder,
  useDestroyReminder,
  useLoadReminders,
} from 'modules/trainings/hooks/useReminders'
import { ReminderPreview } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/SessionsTab/NewSessionModal/ReminderPreview'

interface ReminderCardProps {
  data: SessionData
  setCurrentData: (value: SessionData) => void
  open: boolean
  setOpen: (value: boolean) => void
  reminderData: ReminderValues
  setReminderData: (value: ReminderValues) => void
  initialReminderData: ReminderValues
}

const ReminderCard = ({
  data,
  open,
  setOpen,
  setCurrentData,
  reminderData,
  setReminderData,
  initialReminderData,
}: ReminderCardProps) => {
  const { reminders } = useLoadReminders(Number(data.id))
  // If we don't have sessionId, we are creating a session,
  // so we don't have to load the reminders from the backend.
  const remindersToShow = (data?.id ? reminders : data?.reminders) || []
  const [creating, setCreating] = useState<boolean>(remindersToShow.length === 0)
  const [currentIndex, setCurrentIndex] = useState<number | undefined>(undefined)
  const { buildTime } = useSessionsForm()

  const getReminderDate = (
    startDate: string,
    timeframe: TrainingsReminderTimeframeEnum
  ): string => {
    const localeData = moment.localeData()
    const format = `${localeData.longDateFormat('L')} HH:mm`
    let parsedDate = moment(startDate, format)

    switch (timeframe) {
      case 'oneday':
        parsedDate = parsedDate.subtract(1, 'days')
        break
      case 'onehour':
        parsedDate = parsedDate.subtract(1, 'hours')
        break
      case 'oneweek':
        parsedDate = parsedDate.subtract(1, 'weeks')
        break
      case 'twodays':
        parsedDate = parsedDate.subtract(2, 'days')
        break
      case 'threedays':
        parsedDate = parsedDate.subtract(3, 'days')
        break
      default:
        throw new Error('Invalid timeframe')
    }

    return parsedDate.toISOString()
  }

  const parseTime = (day: string, hour: string) => {
    let time = moment(day, 'MM/DD/YYYY', true)
    if (!time.isValid()) {
      time = moment(day, 'DD/MM/YYYY', true)
    }
    if (!time.isValid()) {
      return null
    }

    const existingHours = moment(hour, 'HH:mm')

    const formattedTime = time
      .set('hour', existingHours.hour())
      .set('minutes', existingHours.minutes())

    return formattedTime.toISOString()
  }

  //convert local ReminderValues to Trainings::Dtos::Reminders::Create used at backend
  const convertReminderValuesToDTO = (reminder: ReminderValues) => ({
    scheduledAt: buildTime(reminder.scheduledAt, reminder.scheduledAtHour || '00:00'),

    name: reminder.name,
    content: reminder.content,
    timeframe: reminder.timeframe,
  })
  const reminderTimeOptions = [
    {
      label: i18n.t(
        'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.timeframe_options.one_day'
      ),
      value: 'oneday',
    },
    {
      label: i18n.t(
        'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.timeframe_options.two_days'
      ),
      value: 'twodays',
    },
    {
      label: i18n.t(
        'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.timeframe_options.three_days'
      ),
      value: 'threedays',
    },
    {
      label: i18n.t(
        'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.timeframe_options.one_hour'
      ),
      value: 'onehour',
    },
    {
      label: i18n.t(
        'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.timeframe_options.one_week'
      ),
      value: 'oneweek',
    },
    {
      label: i18n.t(
        'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.timeframe_options.custom'
      ),
      value: 'custom',
    },
  ]

  const { create } = useCreateReminder()
  const onCreateReminders = async () => {
    track(trackingEvents.training.trainingSession.addReminderConfirm)
    const isEdit = Boolean(data?.id)
    const reminderValues = reminderData
    const newReminder = convertReminderValuesToDTO(reminderValues)

    if (isEdit) {
      const result = await create({
        ...newReminder,
        sessionId: Number(data.id),
      })

      const handled = handleResult(result, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.reminders.create_error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.reminders.create_success'),
        })
        setCreating(false)
      }
    } else {
      const isCustomTimeframe = reminderData.timeframe === 'custom'

      const scheduledAt = isCustomTimeframe
        ? parseTime(reminderData.scheduledAt, reminderData.scheduledAtHour || '00:00')
        : getReminderDate(
            dateBackendDateTimeToUI(String(data.startsAt)),
            reminderData.timeframe ?? 'oneday'
          )

      const newReminder = {
        scheduledAt,
        name: reminderData.name,
        content: reminderData.content,
        timeframe: reminderData.timeframe,
      }
      const updatedReminders = [...(data?.reminders ?? [])]
      if (currentIndex === undefined) {
        updatedReminders.push(newReminder)
      } else {
        updatedReminders.splice(currentIndex, 1, newReminder)
      }

      setReminderData(initialReminderData)
      setCreating(false)
      setCurrentData({ ...data, reminders: updatedReminders })
      setOpen(false)
    }
  }

  const { destroyReminder } = useDestroyReminder()
  const onDeleteReminder = async (reminderId: number, index: number) => {
    if (reminderId) {
      // We have id, delete from backend
      const response = await destroyReminder({ id: reminderId })

      const handled = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.reminders.delete_error'),
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.reminders.delete_success'),
        })
      }
    } else {
      // We don't have id, we are creating a session, delete from memory
      // and don't create when session saved
      const reminders = data.reminders
      reminders.splice(index, 1)
      setCurrentData({ ...data, reminders: reminders })
    }
  }

  const getDate = (isoDate: string) => {
    const date = new Date(isoDate)

    const formatted = `${String(date.getMonth() + 1).padStart(2, '0')}/${String(
      date.getDate()
    ).padStart(2, '0')}/${date.getFullYear()}`

    return formatted
  }

  const getTime = (isoDate: string) => {
    const date = new Date(isoDate)
    const hours = date.getHours()
    const minutes = date.getMinutes()
    const timeFormatted = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    return timeFormatted
  }

  return (
    <Card>
      <Card.Title
        size="small"
        action={
          <TogglerCheckbox
            value={open || remindersToShow?.length > 0}
            onChange={setOpen}
            disabled={!data.startsAt}
          />
        }
      >
        {i18n.t('trainings.trainings_revamp.sessions.new_session_modal.reminders.add_reminder')}
      </Card.Title>
      <Card.Description>
        {i18n.t('trainings.trainings_revamp.sessions.new_session_modal.reminders.description')}
      </Card.Description>
      <Card.ExpandableContent expanded={open || remindersToShow?.length > 0}>
        {remindersToShow?.map((reminder, index) => {
          return (
            <ReminderPreview
              // eslint-disable-next-line no-type-assertion/no-type-assertion
              reminderData={reminder as ReminderValues}
              index={index}
              key={index}
              data={data}
              setCurrentData={setCurrentData}
              setCreating={setCreating}
              setReminderData={setReminderData}
              setCurrentIndex={setCurrentIndex}
              onDelete={onDeleteReminder}
            />
          )
        })}
        {creating || !remindersToShow?.length ? (
          <Box gap="s16">
            <Select
              label={i18n.t('trainings.trainings_revamp.sessions.new_session_modal.reminders.time')}
              none={i18n.t('common.select')}
              options={reminderTimeOptions}
              onChange={(e) =>
                setReminderData({
                  ...reminderData,
                  // eslint-disable-next-line no-type-assertion/no-type-assertion
                  timeframe: e as TrainingsReminderTimeframeEnum,
                })
              }
              mandatory
              value={reminderData.timeframe}
            />
            <TextField
              label={i18n.t('trainings.reminders_table.name')}
              value={reminderData.name || ''}
              onChange={(value: string) => setReminderData({ ...reminderData, name: value })}
              mandatory
            />
            {reminderData.timeframe === 'custom' && (
              <>
                <Text.Normal size="200" color="grey1000" weight="medium">
                  {i18n.t('trainings.reminders_create_modal.scheduled_at')}
                </Text.Normal>
                <Box flexDirection="row" gap="s16">
                  <DayPicker
                    value={reminderData.scheduledAtUi ?? getDate(reminderData.scheduledAt)}
                    label={i18n.t('trainings.reminders_create_modal.date')}
                    onChange={(date) =>
                      setReminderData({
                        ...reminderData,
                        scheduledAt: date,
                        scheduledAtUi: date,
                      })
                    }
                  />
                  <TextField
                    value={reminderData.scheduledAtHour ?? getTime(reminderData.scheduledAt)}
                    label={i18n.t('trainings.reminders_create_modal.time')}
                    type="time"
                    onChange={(value) =>
                      setReminderData({
                        ...reminderData,
                        scheduledAtHour: value,
                      })
                    }
                  />
                </Box>
              </>
            )}
            <>
              <Text.Normal size="200" color="grey1000" weight="medium">
                {i18n.t('trainings.reminders_create_modal.content')}
              </Text.Normal>
              <TextEditor
                placeholder=""
                height="s140"
                initialEditorState={{
                  data: reminderData.content || '',
                }}
                onChange={(value) => setReminderData({ ...reminderData, content: String(value) })}
              />
            </>
            <Box gap="s12" flexDirection="row">
              <Button
                type="secondary"
                onClick={() => {
                  if (remindersToShow?.length > 0) {
                    setCreating(false)
                  } else {
                    setOpen(false)
                  }
                }}
              >
                {i18n.t('common.cancel')}
              </Button>
              <Button
                disabled={!reminderData.timeframe || !reminderData.name}
                onClick={() => onCreateReminders()}
              >
                {i18n.t('trainings.reminders_create_modal.save')}
              </Button>
            </Box>
          </Box>
        ) : null}
        {!!remindersToShow?.length && (
          <SectionButton
            disabled={creating}
            icon="add"
            onClick={() => {
              track(trackingEvents.training.trainingSession.addReminderStart)
              setCreating(true)
            }}
          >
            {i18n.t('trainings.trainings_revamp.sessions.new_session_modal.reminders.add_reminder')}
          </SectionButton>
        )}
      </Card.ExpandableContent>
    </Card>
  )
}

export default ReminderCard
